// src/components/pos/WaiterOrderDrawer.js
// Slide-in order-taking drawer for the web Table Service page.
// Reuses the existing POS ordering hooks/helpers with a modern catalog+cart UI.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useLanguage, getProductName } from '../../context/LanguageContext';
import tenantStorage from '../../services/tenantStorage';
import { orderTrackingService, occupationService, modifierService } from '../../services/api';
import { deductStockForPendingOrder, deductStockForAddedItems, updateOrderTrackingRecords } from '../../utils/OrderHelpers';
import useBusinessDay from '../../hooks/pos/useBusinessDay';
import useProductCatalog from '../../hooks/pos/useProductCatalog';
import useOrderBuilder from '../../hooks/pos/useOrderBuilder';
import AddonSelectionModal from './modals/AddonSelectionModal';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const getDisplayName = (username) => {
    if (username && username.startsWith('employee_')) {
        const name = username.replace('employee_', '');
        return name.replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^[a-z]/, (m) => m.toUpperCase());
    }
    return username || 'Unknown';
};

const stockFor = (p, bulkCalculatedStock) => {
    if (!p) return 0;
    if (p.stock_calculation_type === 'auto') {
        return bulkCalculatedStock[p.product_code]?.available_stock ?? 0;
    }
    return p.quantity === -9999 ? Infinity : p.quantity;
};

const CloseIcon = ({ size = 18, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const SearchIcon = ({ size = 15, color = C.mutedLight }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

/**
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - mode: 'new' | 'append'
 *  - diningOptionName: string
 *  - diningOptionObject: object
 *  - tableNumber: string
 *  - occupation: object | null
 *  - existingOrder: object | null  (required for append; must include order_id + products)
 *  - onOrderPlaced: () => Promise<void> | void
 */
const WaiterOrderDrawer = ({
    isOpen, onClose, mode = 'new',
    diningOptionName, diningOptionObject, tableNumber,
    occupation, existingOrder, onOrderPlaced,
}) => {
    const { tenantConfig, username, getCurrency } = useAuth();
    const currency = getCurrency();
    const { t, language, translationDetails } = useLanguage();

    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const originalProductsRef = useRef([]);
    const catalogLoadedRef = useRef(false);

    const { isBusinessDay, businessDaySalesDate } = useBusinessDay();

    const {
        categories, printableCategories,
        selectedCategory, setSelectedCategory,
        subcategories, selectedSubcategory, setSelectedSubcategory,
        bulkCalculatedStock, productSearch, setProductSearch,
        products, loadCatalog, refreshBulkStock,
    } = useProductCatalog(tenantConfig);

    const diningOptionObjects = useMemo(
        () => (diningOptionName ? { [diningOptionName]: diningOptionObject } : {}),
        [diningOptionName, diningOptionObject],
    );

    const {
        selectedProducts, setSelectedProducts,
        notes, setNotes,
        addServiceCharge, addTax,
        currentProduct,
        currentProductGroups, activeCatalog, modifierTree,
        toggleModifier, modifierUnmet, modifierFlagUnmet, modifierLineTotal,
        addonModalOpen, setAddonModalOpen,
        calculateTotal,
        handleProductWithAddons,
        addProductWithAddons,
        handleUpdateProductQuantity,
        handleRemoveProduct,
        handleUpdateProductNotes,
        setModifierCatalog,
    } = useOrderBuilder({
        tenantConfig, selectedDiningOption: diningOptionName,
        diningOptionObjects, bulkCalculatedStock, products, setError, t,
    });

    // Load catalog once, and (re)seed cart whenever the drawer opens
    useEffect(() => {
        if (!isOpen) return;
        if (!catalogLoadedRef.current) {
            loadCatalog();
            // Feed the modifier catalog once too (Task 17) — without this the
            // builder's modifierCatalog never leaves its empty useState
            // default and every product falls back to the legacy flat
            // add-on path. Routes online/offline the same way loadCatalog
            // does (modifierService.getCatalog() branches internally).
            modifierService.getCatalog()
                .then(setModifierCatalog)
                .catch((err) => console.error('Failed to load modifier catalog:', err));
            catalogLoadedRef.current = true;
        }
        setError('');
        setProductSearch('');
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        if (mode === 'append' && existingOrder?.products) {
            const seed = JSON.parse(JSON.stringify(existingOrder.products));
            originalProductsRef.current = JSON.parse(JSON.stringify(existingOrder.products));
            setSelectedProducts(seed);
            setNotes(existingOrder.notes || '');
        } else {
            originalProductsRef.current = [];
            setSelectedProducts([]);
            setNotes('');
        }
        // Intentionally limited deps — re-init only when the drawer opens or the target order changes.
        // eslint-disable-next-line
    }, [isOpen, mode, tableNumber, existingOrder?.order_id]);

    // Escape to close
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape' && !addonModalOpen) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, addonModalOpen, onClose]);

    const totals = calculateTotal();
    const itemCount = selectedProducts.reduce((s, p) => s + (parseInt(p.quantity) || 1), 0);

    // Product grid — computed locally so "All" + global search work
    // (the hook only populates filteredProducts when a category is selected).
    const displayProducts = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        let list = products;
        if (selectedCategory) list = list.filter(p => p.category === selectedCategory);
        if (selectedSubcategory) list = list.filter(p => p.subcategory === selectedSubcategory);
        if (term) {
            list = list.filter(p =>
                (p.name || '').toLowerCase().includes(term) ||
                (p.product_code || '').toLowerCase().includes(term) ||
                (p.subcategory || '').toLowerCase().includes(term));
        }
        return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [products, selectedCategory, selectedSubcategory, productSearch]);

    const buildTrackingRecords = async (order, staffName) => {
        const allCategories = [...new Set(order.products.map(p => p.category))];
        for (const category of allCategories) {
            const categoryItems = order.products.filter(p => p.category === category);
            if (categoryItems.length === 0) continue;
            try {
                await orderTrackingService.createOrderTracking({
                    transaction_id: null,
                    order_id: order.order_id,
                    category,
                    printed_categories: false,
                    items: categoryItems.map(item => ({
                        name: item.name, quantity: item.quantity,
                        notes: item.notes || null, add_ons: item.add_ons || [],
                    })),
                    table_info: {
                        dining_option: diningOptionName,
                        table_number: tableNumber,
                        staff_name: staffName,
                    },
                    total_items_count: categoryItems.reduce((sum, i) => sum + i.quantity, 0),
                    notes: `Staff: ${staffName}${order.notes ? ` | ${order.notes}` : ''}`,
                });
            } catch (e) {
                console.error(`Failed to create tracking for ${category}:`, e);
            }
        }
    };

    const emitOrderEvents = (orderId, orderType) => {
        const cats = [...new Set(selectedProducts.map(p => p.category))];
        cats.forEach(category => {
            const eventData = { type: 'orderSubmitted', category, orderType, orderId, timestamp: Date.now() };
            localStorage.setItem('orderEvent', JSON.stringify(eventData));
            localStorage.removeItem('orderEvent');
            window.dispatchEvent(new CustomEvent('orderEvent', { detail: eventData }));
        });
    };

    const handleSubmit = async () => {
        if (!isBusinessDay || !businessDaySalesDate) {
            setError(t('no_current_business_day_consult_manager')); return;
        }
        if (!diningOptionName || !tableNumber || selectedProducts.length === 0) {
            setError(t('please_select_table_and_add_products')); return;
        }
        if (mode === 'append' && !existingOrder?.order_id) {
            setError(t('order_id_not_found_refresh_page')); return;
        }
        if (mode === 'new' && occupation?.first_order_at) {
            setError(t('table_already_occupied')); return;
        }
        const tableKey = `${diningOptionName}-${tableNumber}`;
        const staffName = getDisplayName(username);
        try {
            setSubmitting(true);
            const salesDate = await tenantStorage.getState('salesDate');

            if (mode === 'append') {
                const updatedOrder = {
                    ...existingOrder,
                    products: selectedProducts,
                    notes,
                    subtotal: totals.subtotal,
                    total: totals.total,
                    time: format(new Date(), 'HH:mm:ss'),
                };
                await tenantStorage.saveOrder(tableKey, updatedOrder, false, true, existingOrder.order_id, salesDate);
                try {
                    await updateOrderTrackingRecords(
                        originalProductsRef.current, selectedProducts, existingOrder.order_id,
                        existingOrder, tenantConfig, diningOptionName, tableKey,
                        staffName, products, false, { printableCategories },
                    );
                } catch (e) { console.error('updateOrderTrackingRecords failed:', e); }

                // The items this append ADDED have to come off the shelf
                // here — see the same call in
                // pos-app/surfaces/waiter/lib/drawerFlow.js.
                try {
                    await deductStockForAddedItems(
                        originalProductsRef.current, selectedProducts,
                        diningOptionName, tableNumber, username,
                    );
                } catch (e) { console.error('deductStockForAddedItems failed:', e); }

                emitOrderEvents(existingOrder.order_id, 'order_updated');
            } else {
                try {
                    await deductStockForPendingOrder(selectedProducts, diningOptionName, tableNumber, username);
                } catch (e) {
                    console.error('deductStockForPendingOrder failed:', e);
                    setError(t('failed_to_deduct_stock_try_again')); setSubmitting(false); return;
                }
                const pendingOrder = {
                    time: format(new Date(), 'HH:mm:ss'),
                    date: format(new Date(), 'yyyy-MM-dd'),
                    sales_date: salesDate,
                    cashier: staffName,
                    diningOption: diningOptionName,
                    tableNumber,
                    products: [...selectedProducts],
                    subtotal: totals.subtotal,
                    discount: 0,
                    serviceCharge: totals.serviceCharge,
                    total: totals.total,
                    notes,
                    paymentMethod: '',
                    addServiceCharge,
                    addTax,
                    tax: totals.tax,
                    is_submitted: false,
                    is_printed: false,
                };
                const response = await tenantStorage.saveOrder(tableKey, pendingOrder, false, false, null, salesDate);
                const orderId = response.order_id;
                if (!orderId) throw new Error(t('failed_to_get_order_id'));
                await buildTrackingRecords({ ...pendingOrder, order_id: orderId }, staffName);
                emitOrderEvents(orderId, 'new_order');
                // Move the occupation into the "ordered" lifecycle state
                if (occupation?.id && !occupation.first_order_at) {
                    try { await occupationService.markFirstOrder(occupation.id); }
                    catch (e) { console.error('markFirstOrder failed:', e); }
                }
            }

            await refreshBulkStock();
            if (onOrderPlaced) await onOrderPlaced();
            setSubmitting(false);
            onClose();
        } catch (err) {
            console.error('Order submit failed:', err);
            setError(t('failed_to_submit_order'));
            setSubmitting(false);
        }
    };

    const isAppend = mode === 'append';

    return (
        <>
            {/* Scrim */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 40,
                    background: isOpen ? 'rgba(15,23,42,0.45)' : 'transparent',
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease',
                }}
            />
            {/* Drawer */}
            <div
                role="dialog"
                aria-modal="true"
                style={{
                    position: 'fixed', top: 0, right: 0, height: '100dvh',
                    width: 'min(760px, 92vw)', zIndex: 45,
                    background: C.white, fontFamily: FONT,
                    boxShadow: '-12px 0 40px rgba(0,0,0,0.14)',
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.28s cubic-bezier(0.22,1,0.36,1)',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
                    {/* ── Catalog (left) ── */}
                    <div style={{
                        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                        borderRight: `1px solid ${C.border}`,
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
                        }}>
                            <div style={{
                                width: 42, height: 42, borderRadius: 11, background: C.green,
                                color: C.white, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0,
                            }}>
                                {tableNumber}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>
                                    {t('table') || 'Table'} {tableNumber} · {isAppend ? (t('add_items') || 'Add items') : (t('new_order') || 'New Order')}
                                </div>
                                <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
                                    {diningOptionName}
                                    {occupation?.party_size ? ` · ${t('party') || 'Party'} ${occupation.party_size}` : ''}
                                </div>
                            </div>
                            <button onClick={onClose} aria-label={t('close') || 'Close'} style={{
                                marginLeft: 'auto', width: 34, height: 34, borderRadius: 9,
                                border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <CloseIcon size={17} />
                            </button>
                        </div>

                        {/* Search */}
                        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 30, top: '50%', transform: 'translateY(-50%)' }}>
                                <SearchIcon />
                            </span>
                            <input
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                placeholder={t('search_products') || 'Search products…'}
                                style={{
                                    width: '100%', fontFamily: FONT, fontSize: 13,
                                    padding: '10px 13px 10px 34px', border: `1px solid ${C.border}`,
                                    borderRadius: 10, background: C.bg, color: C.text, outline: 'none',
                                }}
                            />
                        </div>

                        {/* Category pills */}
                        {categories.length > 0 && (
                            <div style={{ display: 'flex', gap: 8, padding: '12px 18px 4px', overflowX: 'auto' }}>
                                <button
                                    onClick={() => { setSelectedCategory(null); setSelectedSubcategory(null); }}
                                    style={pillStyle(selectedCategory === null)}
                                >
                                    {t('all') || 'All'}
                                </button>
                                {categories.map(cat => (
                                    <button key={cat}
                                        onClick={() => { setSelectedCategory(cat); setSelectedSubcategory(null); }}
                                        style={pillStyle(selectedCategory === cat)}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Subcategory pills */}
                        {subcategories && subcategories.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, padding: '6px 18px 4px', overflowX: 'auto' }}>
                                {subcategories.map(sub => (
                                    <button key={sub.id}
                                        onClick={() => setSelectedSubcategory(selectedSubcategory === sub.name ? null : sub.name)}
                                        style={{ ...pillStyle(selectedSubcategory === sub.name), fontSize: 11, padding: '5px 11px' }}
                                    >
                                        {sub.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Product grid */}
                        <div style={{
                            flex: 1, overflowY: 'auto', padding: '14px 18px',
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
                            gap: 12, alignContent: 'start',
                        }}>
                            {displayProducts.length === 0 ? (
                                <div style={{
                                    gridColumn: '1 / -1', textAlign: 'center', padding: '48px 16px',
                                    color: C.mutedLight, fontSize: 13,
                                }}>
                                    {productSearch
                                        ? (t('no_products_found') || 'No products found')
                                        : (t('no_products_in_category') || 'No products in this category')}
                                </div>
                            ) : displayProducts.map(p => {
                                const stock = stockFor(p, bulkCalculatedStock);
                                const out = stock !== Infinity && stock <= 0;
                                return (
                                    <button key={p.product_code || p.id}
                                        disabled={out}
                                        onClick={() => handleProductWithAddons(p)}
                                        style={{
                                            textAlign: 'left', fontFamily: FONT, cursor: out ? 'not-allowed' : 'pointer',
                                            border: `1px solid ${C.border}`, borderRadius: 13, padding: 13,
                                            background: C.white, opacity: out ? 0.5 : 1,
                                            transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                                        }}
                                        onMouseEnter={(e) => { if (!out) { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                                    >
                                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.25 }}>
                                            {getProductName(p, language, translationDetails) || p.name}
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: C.green, marginTop: 6 }}>
                                            {currency}{parseFloat(p.selling_price || 0).toFixed(2)}
                                        </div>
                                        {out && (
                                            <div style={{ fontSize: 10, fontWeight: 700, color: C.red, marginTop: 4 }}>
                                                {t('out_of_stock') || 'Out of stock'}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Cart (right) ── */}
                    <div style={{ width: 330, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fcfdfc' }}>
                        <div style={{
                            padding: '16px 18px', borderBottom: `1px solid ${C.border}`,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: C.muted,
                        }}>
                            <span>{t('order') || 'Order'}</span>
                            <span>{itemCount} {itemCount === 1 ? (t('item') || 'item') : (t('items') || 'items')}</span>
                        </div>

                        {error && (
                            <div style={{
                                margin: '10px 14px 0', padding: '8px 12px', borderRadius: 9,
                                background: C.redLight, border: '1px solid #fecaca',
                                color: C.red, fontSize: 12, fontWeight: 600,
                            }}>
                                {error}
                            </div>
                        )}

                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px' }}>
                            {selectedProducts.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 12px', color: C.mutedLight, fontSize: 13 }}>
                                    {t('tap_products_to_add') || 'Tap products to add them to the order'}
                                </div>
                            ) : selectedProducts.map(item => {
                                const key = item.uniqueId || item.product_code;
                                const linePrice = (parseFloat(item.selling_price) || 0) + (parseFloat(item.add_on_total) || 0);
                                return (
                                    <div key={key} style={{
                                        padding: 12, borderRadius: 11, border: `1px solid ${C.border}`,
                                        marginBottom: 9, background: C.white,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                                {getProductName(item, language, translationDetails) || item.name}
                                            </div>
                                            <div style={{ fontSize: 13, fontWeight: 800, color: C.text, whiteSpace: 'nowrap' }}>
                                                {currency}{(linePrice * (parseInt(item.quantity) || 1)).toFixed(2)}
                                            </div>
                                        </div>
                                        {item.add_ons && item.add_ons.length > 0 && (
                                            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                                                {item.add_ons.map(a => a?.name).filter(Boolean).join(', ')}
                                            </div>
                                        )}
                                        <input
                                            value={item.notes || ''}
                                            onChange={(e) => handleUpdateProductNotes(key, e.target.value)}
                                            placeholder={t('add_note') || 'Add note…'}
                                            style={{
                                                width: '100%', marginTop: 7, fontFamily: FONT, fontSize: 11,
                                                padding: '6px 8px', border: `1px solid ${C.border}`,
                                                borderRadius: 7, background: C.bg, color: C.text, outline: 'none',
                                            }}
                                        />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 9 }}>
                                            <div style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 10,
                                                border: `1px solid ${C.border}`, borderRadius: 8, padding: '3px 8px',
                                            }}>
                                                <button onClick={() => handleUpdateProductQuantity(key, (parseInt(item.quantity) || 1) - 1)}
                                                    disabled={(parseInt(item.quantity) || 1) <= 1}
                                                    style={qtyBtn}>−</button>
                                                <span style={{ fontSize: 13, fontWeight: 700, minWidth: 14, textAlign: 'center' }}>{item.quantity}</span>
                                                <button onClick={() => handleUpdateProductQuantity(key, (parseInt(item.quantity) || 1) + 1)}
                                                    style={qtyBtn}>+</button>
                                            </div>
                                            <button onClick={() => handleRemoveProduct(key)}
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                    color: C.red, fontSize: 11, fontWeight: 700, fontFamily: FONT,
                                                }}>
                                                {t('remove') || 'Remove'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer / totals */}
                        <div style={{ borderTop: `1px solid ${C.border}`, padding: '16px 18px', background: C.white }}>
                            <div style={rowStyle}><span>{t('subtotal') || 'Subtotal'}</span><span>{currency}{totals.subtotal.toFixed(2)}</span></div>
                            {addServiceCharge && (
                                <div style={rowStyle}><span>{t('service_charge') || 'Service charge'}</span><span>{currency}{totals.serviceCharge.toFixed(2)}</span></div>
                            )}
                            {addTax && (
                                <div style={rowStyle}><span>{t('tax') || 'Tax'}</span><span>{currency}{totals.tax.toFixed(2)}</span></div>
                            )}
                            <div style={{ ...rowStyle, fontSize: 17, fontWeight: 800, color: C.text, margin: '10px 0 14px' }}>
                                <span>{t('total') || 'Total'}</span><span>{currency}{totals.total.toFixed(2)}</span>
                            </div>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting || selectedProducts.length === 0}
                                style={{
                                    width: '100%', background: (submitting || selectedProducts.length === 0) ? C.mutedLight : C.green,
                                    color: C.white, border: 'none', fontFamily: FONT, fontSize: 15, fontWeight: 800,
                                    padding: 14, borderRadius: 12, cursor: (submitting || selectedProducts.length === 0) ? 'not-allowed' : 'pointer',
                                    transition: 'background 0.15s',
                                }}
                            >
                                {submitting
                                    ? (t('sending') || 'Sending…')
                                    : isAppend ? (t('update_order') || 'Update order') : (t('send_to_kitchen') || 'Send to kitchen')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AddonSelectionModal
                isOpen={addonModalOpen}
                onClose={() => setAddonModalOpen(false)}
                currentProduct={currentProduct}
                groupIds={currentProductGroups}
                catalog={activeCatalog}
                tree={modifierTree}
                onToggle={toggleModifier}
                unmet={modifierUnmet}
                flagUnmet={modifierFlagUnmet}
                lineTotal={modifierLineTotal}
                onAdd={addProductWithAddons}
                currency={currency}
            />
        </>
    );
};

const pillStyle = (active) => ({
    fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700,
    padding: '7px 14px', borderRadius: 20, cursor: 'pointer',
    border: active ? `1px solid ${C.greenDark}` : `1px solid ${C.border}`,
    background: active ? C.greenDark : C.white,
    color: active ? C.white : C.muted,
});

const qtyBtn = {
    border: 'none', background: 'transparent', fontSize: 16, fontWeight: 800,
    color: C.green, cursor: 'pointer', width: 18, lineHeight: 1, fontFamily: FONT,
};

const rowStyle = {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 12.5, color: C.muted, marginBottom: 7, fontWeight: 600,
};

export default WaiterOrderDrawer;
